import React, { useMemo, useState } from 'react';

// Import bookmaker logos
import sportsbetLogo from '../../assets/sb.png';
import tabLogo from '../../assets/tab.jpg';
import pointsbetLogo from '../../assets/pb.png';
import dabbleLogo from '../../assets/dabble.webp';
import nedsLogo from '../../assets/neds.jpg';
import betrightLogo from '../../assets/betright.jpg';
import tabtouchLogo from '../../assets/tabtouch.webp';
import bet365Logo from '../../assets/bet365.png';
import betmakersLogo from '../../assets/betmakers.png';
import ladbrokesLogo from '../../assets/ladbrokes.webp';
import betrLogo from '../../assets/betr.jpg';
import picklebetLogo from '../../assets/picklebet.jpeg';
import playupLogo from '../../assets/playup.png';
import unibetLogo from '../../assets/unibet.webp';

function BookmakerBreakdown({ bets }) {
    const [sortBy, setSortBy] = useState('pl'); // 'pl', 'winRate', 'bets'

    // Bookmaker groupings (companies that own multiple brands)
    const bookmakerGroups = {
        'Ladbrokes': 'Ladbrokes/Neds',
        'Neds': 'Ladbrokes/Neds',
    };

    // Bookmaker logo mapping
    const bookieLogos = {
        'Sportsbet': sportsbetLogo,
        'TAB': tabLogo,
        'Pointsbet': pointsbetLogo,
        'Dabble': dabbleLogo,
        'Ladbrokes/Neds': ladbrokesLogo,
        'Neds': nedsLogo,
        'Betright': betrightLogo,
        'TABtouch': tabtouchLogo,
        'Bet365': bet365Logo,
        'bet365': bet365Logo,
        'Betmakers': betmakersLogo,
        'Betr': betrLogo,
        'Picklebet': picklebetLogo,
        'PlayUp': playupLogo,
        'Unibet': unibetLogo,
    };

    const bookmakerData = useMemo(() => {
        const grouped = {};

        bets.forEach(bet => {
            const rawBookie = bet.bookie || 'Unknown';
            // Map to grouped name if applicable
            const bookie = bookmakerGroups[rawBookie] || rawBookie;
            
            if (!grouped[bookie]) {
                grouped[bookie] = {
                    name: bookie,
                    bets: 0,
                    wins: 0,
                    losses: 0,
                    totalStaked: 0,
                    totalReturned: 0,
                    pl: 0
                };
            }

            grouped[bookie].bets++;
            // Exclude bonus bets from staked totals
            if (!bet.bonus_bet) {
                grouped[bookie].totalStaked += bet.stake;
            }
            grouped[bookie].pl += bet.pl;
            
            // Only count returned for settled bets (not pending)
            if (bet.result !== 'pending') {
                grouped[bookie].totalReturned += (bet.bonus_bet ? 0 : bet.stake) + bet.pl;
            }

            if (bet.result === 'win') grouped[bookie].wins++;
            if (bet.result === 'loss') grouped[bookie].losses++;
        });

        return Object.values(grouped)
            .map(b => ({
                ...b,
                winRate: b.bets > 0 ? (b.wins / (b.wins + b.losses)) * 100 : 0,
                roi: b.totalStaked > 0 ? (b.pl / b.totalStaked) * 100 : 0
            }))
            .sort((a, b) => {
                if (sortBy === 'pl') return b.pl - a.pl;
                if (sortBy === 'roi') return b.roi - a.roi;
                return 0;
            });
    }, [bets, sortBy]);

    // Data sorted by P/L for the chart (always)
    const chartData = useMemo(() => {
        return [...bookmakerData].sort((a, b) => b.pl - a.pl);
    }, [bookmakerData]);

    const maxAbsPL = Math.max(...bookmakerData.map(b => Math.abs(b.pl)), 1);
    const totalPL = bookmakerData.reduce((sum, b) => sum + b.pl, 0);

    const formatCurrency = (value) => {
        const prefix = value >= 0 ? '+' : '';
        return `${prefix}$${Math.abs(value).toLocaleString('en-US', { minimumFractionDigits: 0, maximumFractionDigits: 0 })}`;
    };

    const formatCurrencyFull = (value) => {
        const prefix = value >= 0 ? '+' : '';
        return `${prefix}$${Math.abs(value).toLocaleString('en-US', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`;
    };

    // Bookmaker colors
    const getBookieColor = (name) => {
        const colors = {
            'Sportsbet': '#f97316',
            'TAB': '#3b82f6',
            'Pointsbet': '#8b5cf6',
            'Dabble': '#ec4899',
            'Ladbrokes/Neds': '#e11d48',
            'Neds': '#ef4444',
            'Betright': '#22c55e',
            'TABtouch': '#06b6d4',
            'bet365': '#84cc16',
            'Bet365': '#84cc16',
            'Ladbrokes': '#e11d48',
            'Betmakers': '#0ea5e9',
            'Other': '#64748b'
        };
        return colors[name] || '#64748b';
    };

    return (
        <div className="bg-slate-900/50 rounded-2xl border border-slate-700/50 p-4 md:p-6">
            {/* Header */}
            <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-2 mb-4">
                <div className="flex items-center justify-between sm:block">
                    <div>
                        <h3 className="text-base md:text-lg font-semibold text-white mb-0.5 md:mb-1">Bookmaker Performance</h3>
                        <p className="text-xs md:text-sm text-slate-400 hidden sm:block">Results breakdown by bookie</p>
                    </div>
                    {/* P/L value - shown inline on mobile */}
                    <div className={`text-lg md:text-xl font-bold sm:hidden ${totalPL >= 0 ? 'text-emerald-400' : 'text-red-400'}`}>
                        {formatCurrencyFull(totalPL)}
                    </div>
                </div>
                {/* P/L value - hidden on mobile, shown on sm+ */}
                <div className={`hidden sm:block text-lg md:text-xl font-bold ${totalPL >= 0 ? 'text-emerald-400' : 'text-red-400'}`}>
                    {formatCurrencyFull(totalPL)}
                </div>
            </div>

            {/* P/L by Bookmaker - Horizontal Bar Chart */}
            <div className="mb-6">
                <div className="text-xs text-slate-500 uppercase tracking-wider mb-3">P/L by Bookmaker</div>
                <div className="space-y-2">
                    {chartData.map((bookie) => {
                        const barWidth = (Math.abs(bookie.pl) / maxAbsPL) * 50; // 50% max width for each side
                        const isPositive = bookie.pl >= 0;
                        
                        return (
                            <div key={bookie.name} className="flex items-center gap-2 group" title={bookie.name}>
                                {/* Bookmaker logo - left side */}
                                <div className="w-8 flex items-center justify-center">
                                    {bookieLogos[bookie.name] ? (
                                        <img 
                                            src={bookieLogos[bookie.name]} 
                                            alt={bookie.name}
                                            className="w-6 h-6 rounded object-cover"
                                        />
                                    ) : (
                                        <div 
                                            className="w-6 h-6 rounded flex items-center justify-center text-[10px] font-bold text-white"
                                            style={{ backgroundColor: getBookieColor(bookie.name) }}
                                        >
                                            {bookie.name.charAt(0)}
                                        </div>
                                    )}
                                </div>
                                
                                {/* Bar chart - centered with negative left, positive right */}
                                <div className="flex-1 flex items-center">
                                    {/* Negative side (left) */}
                                    <div className="w-1/2 flex justify-end">
                                        {!isPositive && (
                                            <div 
                                                className="h-5 rounded-l-sm bg-gradient-to-l from-red-500 to-red-600 transition-all duration-300 group-hover:from-red-400 group-hover:to-red-500"
                                                style={{ 
                                                    width: `${barWidth}%`,
                                                    boxShadow: '0 0 10px rgba(239, 68, 68, 0.3)'
                                                }}
                                            />
                                        )}
                                    </div>
                                    
                                    {/* Center line */}
                                    <div className="w-px h-6 bg-slate-600" />
                                    
                                    {/* Positive side (right) */}
                                    <div className="w-1/2 flex justify-start">
                                        {isPositive && (
                                            <div 
                                                className="h-5 rounded-r-sm bg-gradient-to-r from-emerald-500 to-teal-500 transition-all duration-300 group-hover:from-emerald-400 group-hover:to-teal-400"
                                                style={{ 
                                                    width: `${barWidth}%`,
                                                    boxShadow: '0 0 10px rgba(16, 185, 129, 0.3)'
                                                }}
                                            />
                                        )}
                                    </div>
                                </div>
                                
                                {/* P/L value - right side */}
                                <div className="w-20 text-left">
                                    <span className={`text-xs font-semibold ${isPositive ? 'text-emerald-400' : 'text-red-400'}`}>
                                        {formatCurrency(bookie.pl)}
                                    </span>
                                </div>
                            </div>
                        );
                    })}
                </div>
                
                {/* Chart axis labels */}
                <div className="flex items-center mt-2 pl-10 pr-20">
                    <div className="w-1/2 flex justify-between text-[10px] text-slate-600 pr-1">
                        <span>-${(maxAbsPL/1000).toFixed(0)}k</span>
                        <span>-${(maxAbsPL/2000).toFixed(0)}k</span>
                    </div>
                    <div className="w-px" />
                    <div className="w-1/2 flex justify-between text-[10px] text-slate-600 pl-1">
                        <span>+${(maxAbsPL/2000).toFixed(0)}k</span>
                        <span>+${(maxAbsPL/1000).toFixed(0)}k</span>
                    </div>
                </div>
            </div>

            {/* Divider */}
            <div className="border-t border-slate-700/50 my-4" />

            {/* Performance by Bookmaker - Detailed List */}
            <div>
                <div className="flex items-center justify-between mb-3">
                    <div className="text-xs text-slate-500 uppercase tracking-wider">Performance by Bookmaker</div>
                    <div className="flex items-center gap-2">
                        <span className="text-xs text-slate-500">Sort:</span>
                        <select
                            value={sortBy}
                            onChange={(e) => setSortBy(e.target.value)}
                            className="px-2 py-1 bg-slate-800 border border-slate-700 rounded-lg text-xs text-white focus:outline-none focus:border-emerald-500"
                        >
                            <option value="pl">P/L</option>
                            <option value="roi">ROI</option>
                        </select>
                    </div>
                </div>

                {/* Bookmaker List */}
                <div className="space-y-2 max-h-[340px] overflow-y-auto pr-1">
                    {bookmakerData.length === 0 ? (
                        <div className="text-center py-8 text-slate-500">
                            No bookmaker data available
                        </div>
                    ) : (
                        bookmakerData.map((bookie) => (
                            <div 
                                key={bookie.name}
                                className="bg-slate-800/30 rounded-lg p-3 border border-slate-700/30 hover:border-slate-600/50 transition-all"
                            >
                                <div className="flex items-center justify-between">
                                    <div className="flex items-center gap-2.5">
                                        {/* Bookie logo */}
                                        {bookieLogos[bookie.name] ? (
                                            <img 
                                                src={bookieLogos[bookie.name]} 
                                                alt={bookie.name}
                                                className="w-7 h-7 rounded-md object-cover"
                                            />
                                        ) : (
                                            <div 
                                                className="w-7 h-7 rounded-md flex items-center justify-center text-xs font-bold text-white"
                                                style={{ backgroundColor: getBookieColor(bookie.name) }}
                                            >
                                                {bookie.name.charAt(0)}
                                            </div>
                                        )}
                                        <div>
                                            <div className="text-sm text-white font-medium">{bookie.name}</div>
                                        </div>
                                    </div>
                                    <div className={`text-sm font-bold ${bookie.pl >= 0 ? 'text-emerald-400' : 'text-red-400'}`}>
                                        {formatCurrencyFull(bookie.pl)}
                                    </div>
                                </div>
                                
                                {/* Stats rows */}
                                <div className="mt-2 pt-2 border-t border-slate-700/20 space-y-1.5">
                                    {/* Row 1: Bets and Win Rate */}
                                    <div className="flex items-center justify-between text-xs">
                                        <div className="flex items-center gap-4">
                                            <span className="text-slate-400">
                                                <span className="text-slate-300">{bookie.bets}</span> bets
                                            </span>
                                            <span className="text-slate-400">
                                                <span className="text-slate-300">{bookie.wins}W</span> - <span className="text-slate-300">{bookie.losses}L</span>
                                            </span>
                                            <span className="text-slate-400">
                                                Win Rate: <span className="text-slate-300">{bookie.winRate.toFixed(1)}%</span>
                                            </span>
                                        </div>
                                        <div className="text-slate-500">
                                            ROI: <span className={`font-medium ${bookie.roi >= 0 ? 'text-emerald-400' : 'text-red-400'}`}>
                                                {bookie.roi >= 0 ? '+' : ''}{bookie.roi.toFixed(1)}%
                                            </span>
                                        </div>
                                    </div>
                                    {/* Row 2: Staked and Returned */}
                                    <div className="flex items-center justify-between text-xs">
                                        <div className="flex items-center gap-4">
                                            <span className="text-slate-400">
                                                Staked: <span className="text-slate-300">${bookie.totalStaked.toLocaleString('en-US', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}</span>
                                            </span>
                                            <span className="text-slate-400">
                                                Returned: <span className="text-slate-300">${bookie.totalReturned.toLocaleString('en-US', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}</span>
                                            </span>
                                        </div>
                                    </div>
                                </div>
                            </div>
                        ))
                    )}
                </div>
            </div>
        </div>
    );
}

export default BookmakerBreakdown;
